// src/services/imageService.ts
// Serviço centralizado para gerenciamento de URLs de imagens

import { STORAGE_CONFIG, IMAGE_TRANSFORMATIONS, FOLDER_MAPPING } from '../config/storage.config';
import imageUrlsMap from '../config/imageUrls.json';

type TransformationType = keyof typeof IMAGE_TRANSFORMATIONS;

interface ImageUrlsMap {
  [category: string]: {
    [fileName: string]: string;
  };
}

const urls = imageUrlsMap as ImageUrlsMap;

// ============================================
// VERIFICAR SE EXISTE NO JSON (PRIORIDADE)
// ============================================

/**
 * Verifica se a imagem existe no mapeamento JSON
 * Isso permite usar URLs de qualquer provider (ImgBB, Cloudinary, etc)
 */
function getFromJsonMapping(folder: string, filename: string): string | null {
  // Tenta encontrar no JSON
  if (urls[folder] && urls[folder][filename]) {
    return urls[folder][filename];
  }
  
  // Tenta variações comuns do nome do arquivo
  const variations = [
    filename,
    filename.toLowerCase(),
    filename.replace('.jpeg', '.jpg'),
    filename.replace('.jpg', '.jpeg'),
    filename.charAt(0).toUpperCase() + filename.slice(1), // Capitalizado
  ];
  
  for (const variant of variations) {
    if (urls[folder] && urls[folder][variant]) {
      return urls[folder][variant];
    }
  }
  
  return null;
}

// ============================================
// CLOUDINARY URL BUILDER
// ============================================

/**
 * Constrói URL do Cloudinary com transformações
 */
function buildCloudinaryUrl(
  folder: string,
  filename: string,
  transformation?: TransformationType
): string {
  const { cloudName, baseFolder } = STORAGE_CONFIG.cloudinary;
  const trans = transformation ? IMAGE_TRANSFORMATIONS[transformation] : IMAGE_TRANSFORMATIONS.responsive;
  
  // Converte objeto de transformação para string de URL
  const transformParams = Object.entries(trans)
    .map(([key, value]) => {
      const keyMap: Record<string, string> = {
        width: 'w',
        height: 'h',
        crop: 'c',
        quality: 'q',
        format: 'f',
        gravity: 'g',
        dpr: 'dpr',
      };
      return `${keyMap[key] || key}_${value}`;
    })
    .join(',');
  
  // Remove extensão do filename para Cloudinary
  const nameWithoutExt = filename.replace(/\.[^.]+$/, '');
  
  return `https://res.cloudinary.com/${cloudName}/image/upload/${transformParams}/${baseFolder}/${folder}/${nameWithoutExt}`;
}

// ============================================
// IMGBB URL GETTER (ATUAL)
// ============================================

/**
 * Obtém URL do ImgBB a partir do mapeamento JSON
 */
function getImgbbUrl(folder: string, filename: string): string {
  if (urls[folder] && urls[folder][filename]) {
    return urls[folder][filename];
  }
  
  // Tenta variações comuns do nome do arquivo
  const variations = [
    filename,
    filename.toLowerCase(),
    filename.replace('.jpeg', '.jpg'),
    filename.replace('.jpg', '.jpeg'),
  ];
  
  for (const variant of variations) {
    if (urls[folder] && urls[folder][variant]) {
      return urls[folder][variant];
    }
  }
  
  console.warn(`[ImageService] Imagem não encontrada: ${folder}/${filename}`);
  return '';
}

// ============================================
// API PÚBLICA DO SERVIÇO
// ============================================

/**
 * Obtém a URL de uma imagem baseado no provedor configurado
 * SEMPRE verifica primeiro o JSON de mapeamento (URLs já migradas)
 * 
 * @param folder - Nome da pasta (ex: 'students', 'gallery')
 * @param filename - Nome do arquivo (ex: 'arthur.jpeg')
 * @param transformation - Tipo de transformação (apenas Cloudinary)
 * @returns URL da imagem
 * 
 * @example
 * getImageUrl('assetsAlunosImg', 'arthur.jpeg')
 */
export function getImageUrl(
  folder: string,
  filename: string,
  transformation?: TransformationType
): string {
  // 1. SEMPRE verifica primeiro no JSON (prioridade máxima)
  // Isso permite manter URLs de qualquer provider no mesmo projeto
  const jsonUrl = getFromJsonMapping(folder, filename);
  if (jsonUrl) {
    return jsonUrl;
  }
  
  // 2. Se não encontrou no JSON, usa o provider configurado como fallback
  const provider = STORAGE_CONFIG.provider;
  
  switch (provider) {
    case 'cloudinary':
      // Mapeia pasta antiga para nova se necessário
      const cloudinaryFolder = FOLDER_MAPPING[folder] || folder;
      return buildCloudinaryUrl(cloudinaryFolder, filename, transformation);
    
    case 'firebase':
      // Implementar quando necessário
      const bucket = STORAGE_CONFIG.firebase.bucket;
      const baseFolder = STORAGE_CONFIG.firebase.baseFolder;
      return `https://storage.googleapis.com/${bucket}/${baseFolder}/${folder}/${filename}`;
    
    case 'imgbb':
    case 'local':
    default:
      // Fallback para path local
      return `${STORAGE_CONFIG.local.basePath}/${folder}/${filename}`;
  }
}

/**
 * Obtém todas as URLs de imagens de uma pasta
 * 
 * @param folder - Nome da pasta
 * @returns Array de objetos com filename e url
 */
export function getFolderImages(folder: string): Array<{ filename: string; url: string }> {
  // Sempre usa o JSON se disponível
  if (urls[folder]) {
    return Object.entries(urls[folder]).map(([filename, url]) => ({
      filename,
      url,
    }));
  }
  
  // Pasta não encontrada no JSON
  console.warn(`[ImageService] Pasta não encontrada no mapeamento: ${folder}`);
  return [];
}

/**
 * Gera srcSet responsivo para imagens (apenas Cloudinary)
 * 
 * @param folder - Nome da pasta
 * @param filename - Nome do arquivo
 * @returns String srcSet para uso em <img>
 */
export function getResponsiveSrcSet(folder: string, filename: string): string {
  if (STORAGE_CONFIG.provider !== 'cloudinary') {
    return '';
  }
  
  const widths = [320, 640, 960, 1280, 1920];
  const { cloudName, baseFolder } = STORAGE_CONFIG.cloudinary;
  const nameWithoutExt = filename.replace(/\.[^.]+$/, '');
  const cloudinaryFolder = FOLDER_MAPPING[folder] || folder;
  
  return widths
    .map(w => `https://res.cloudinary.com/${cloudName}/image/upload/w_${w},q_auto,f_auto/${baseFolder}/${cloudinaryFolder}/${nameWithoutExt} ${w}w`)
    .join(', ');
}

/**
 * Obtém URL de placeholder/blur para lazy loading (apenas Cloudinary)
 * 
 * @param folder - Nome da pasta
 * @param filename - Nome do arquivo
 * @returns URL de imagem tiny para blur placeholder
 */
export function getPlaceholderUrl(folder: string, filename: string): string {
  if (STORAGE_CONFIG.provider !== 'cloudinary') {
    return '';
  }
  
  const { cloudName, baseFolder } = STORAGE_CONFIG.cloudinary;
  const nameWithoutExt = filename.replace(/\.[^.]+$/, '');
  const cloudinaryFolder = FOLDER_MAPPING[folder] || folder;
  
  return `https://res.cloudinary.com/${cloudName}/image/upload/w_50,q_30,e_blur:1000,f_auto/${baseFolder}/${cloudinaryFolder}/${nameWithoutExt}`;
}

// ============================================
// HELPER: EXTRAIR PASTA E FILENAME DE PATH
// ============================================

/**
 * Extrai folder e filename de um path local
 * 
 * @param localPath - Path como '/assets/fotosGaleria/foto1.jpeg'
 * @returns { folder, filename } ou null se inválido
 */
export function parseLocalPath(localPath: string): { folder: string; filename: string } | null {
  // Remove /assets/ do início se existir
  const cleanPath = localPath.replace(/^\/assets\//, '');
  const parts = cleanPath.split('/');
  
  if (parts.length < 2) {
    return null;
  }
  
  const filename = parts.pop()!;
  const folder = parts.join('/');
  
  return { folder, filename };
}

/**
 * Converte um path local para URL externa
 * Útil para migração gradual
 * 
 * @param localPath - Path como '/assets/fotosGaleria/foto1.jpeg'
 * @param transformation - Transformação opcional
 * @returns URL externa ou path original se não encontrado
 */
export function convertLocalToExternal(
  localPath: string,
  transformation?: TransformationType
): string {
  const parsed = parseLocalPath(localPath);
  
  if (!parsed) {
    return localPath;
  }
  
  const url = getImageUrl(parsed.folder, parsed.filename, transformation);
  return url || localPath;
}
